import styled from "styled-components";

import { useMe } from "../api/hooks";
import type { Badge, HeatmapCell, LeaderboardCategory } from "../api/types";
import { Card, Chip, ErrorNotice, Loading, SectionLabel } from "../components/primitives";
import { getUserPhotoUrl } from "../telegram/sdk";
import { theme } from "../theme";

// Category → glyph + accent color (caregiver/solver/quest), shared with the boards.
const CAT_META: Record<LeaderboardCategory, { icon: string; color: string }> = {
  caregiver: { icon: "◯", color: theme.color.satiety },
  solver: { icon: "?", color: theme.color.happiness },
  quest: { icon: "⌬", color: theme.color.accent },
};
const CATEGORIES = Object.keys(CAT_META) as LeaderboardCategory[];

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space(4)};
`;

const Section = styled.div``;

const Header = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
`;

const Note = styled.span`
  font-family: ${({ theme }) => theme.font.mono};
  font-size: 11px;
  color: ${({ theme }) => theme.color.textDim};
`;

// Identity card with a corner glow.
const IdentityCard = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 18px;
  border-radius: ${({ theme }) => theme.radius.xl};
  border: 1px solid ${({ theme }) => theme.color.border};
  background:
    radial-gradient(120% 80% at 100% 0%, rgba(0, 152, 234, 0.18), transparent 55%),
    ${({ theme }) => theme.color.surface};
`;

const Avatar = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 50%;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme }) => theme.color.accent};
  color: #fff;
  font-size: 26px;
  font-weight: 700;
  box-shadow: 0 8px 24px ${({ theme }) => theme.color.accentGlow};
`;

const AvatarImg = styled.img`
  width: 64px;
  height: 64px;
  border-radius: 50%;
  flex-shrink: 0;
  object-fit: cover;
  box-shadow: 0 8px 24px ${({ theme }) => theme.color.accentGlow};
`;

const IdentityBody = styled.div`
  flex: 1;
  min-width: 0;
`;

const Handle = styled.div`
  font-size: 20px;
  font-weight: 700;
  letter-spacing: -0.4px;
`;

const Sub = styled.div`
  font-family: ${({ theme }) => theme.font.mono};
  font-size: 11px;
  color: ${({ theme }) => theme.color.textDim};
  margin-top: 2px;
`;

const RankChips = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.space(2)};
  flex-wrap: wrap;
  margin-top: ${({ theme }) => theme.space(2)};
`;

const Scores = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: ${({ theme }) => theme.space(2)};
`;

const ScoreCell = styled.div`
  position: relative;
  overflow: hidden;
  padding: 14px 12px;
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.color.surface};
  border: 1px solid ${({ theme }) => theme.color.border};
`;

const ScoreHead = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  margin-bottom: 8px;
`;

const ScoreIcon = styled.span<{ $color: string }>`
  color: ${({ $color }) => $color};
  font-family: ${({ theme }) => theme.font.mono};
  font-size: 12px;
`;

const ScoreLabel = styled.span`
  font-family: ${({ theme }) => theme.font.mono};
  font-size: 10px;
  letter-spacing: 0.03em;
  color: ${({ theme }) => theme.color.textDim};
`;

const ScoreVal = styled.div`
  font-size: 20px;
  font-weight: 700;
  letter-spacing: -0.5px;
  color: ${({ theme }) => theme.color.text};
`;

const ScoreUnderline = styled.div<{ $color: string }>`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 2px;
  background: ${({ $color }) => $color};
  opacity: 0.6;
`;

// Contribution graph — GitHub-style: weeks as columns, 7 day-rows, filled
// column-by-column. A fixed column count keeps it a clean rectangle.
const ContribGrid = styled.div<{ $cols: number }>`
  display: grid;
  grid-auto-flow: column;
  grid-template-columns: repeat(${({ $cols }) => $cols}, 1fr);
  grid-template-rows: repeat(7, 1fr);
  gap: 3px;
`;

const HeatCell = styled.div<{ $alpha: number }>`
  aspect-ratio: 1 / 1;
  border-radius: 2px;
  background: ${({ $alpha }) =>
    $alpha <= 0 ? "rgba(255, 255, 255, 0.04)" : `rgba(0, 152, 234, ${$alpha})`};
`;

const ContribFoot = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 12px;
`;

const FootText = styled.div`
  font-family: ${({ theme }) => theme.font.mono};
  font-size: 10px;
  color: ${({ theme }) => theme.color.textFaint};
`;

const Legend = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  font-family: ${({ theme }) => theme.font.mono};
  font-size: 10px;
  color: ${({ theme }) => theme.color.textFaint};
`;

const Swatch = styled.span<{ $alpha: number }>`
  width: 9px;
  height: 9px;
  border-radius: 2px;
  background: ${({ $alpha }) => `rgba(0, 152, 234, ${$alpha})`};
`;

const Badges = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: ${({ theme }) => theme.space(2)};
`;

const BadgeCell = styled.div<{ $earned: boolean; $color: string }>`
  aspect-ratio: 1 / 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ $earned, $color }) => ($earned ? `${$color}18` : "rgba(255, 255, 255, 0.02)")};
  border: 1px solid ${({ theme, $earned, $color }) => ($earned ? `${$color}55` : theme.color.border)};
  opacity: ${({ $earned }) => ($earned ? 1 : 0.4)};
  text-align: center;
  padding: 4px;
`;

const BadgeSym = styled.div<{ $earned: boolean }>`
  font-size: 18px;
  filter: ${({ $earned }) => ($earned ? "none" : "grayscale(1)")};
`;

const BadgeName = styled.div<{ $earned: boolean; $color: string }>`
  font-family: ${({ theme }) => theme.font.mono};
  font-size: 8.5px;
  letter-spacing: 0.02em;
  color: ${({ theme, $earned, $color }) => ($earned ? $color : theme.color.textFaint)};
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const LEGEND_ALPHAS = [0.08, 0.2, 0.4, 0.7, 1];
const DAYS_PER_WEEK = 7;
// Fixed 7×12 rectangle (matches the design). A constant column count keeps the
// grid a clean rectangle even when the API returns few/no heatmap days, instead
// of collapsing to a single full-width column of oversized cells.
const HEAT_WEEKS = 12;
const HEAT_DAYS = HEAT_WEEKS * DAYS_PER_WEEK;

function rankLabel(rank: number | null | undefined): string {
  return rank == null ? "—" : `#${rank}`;
}

// Badges carry no glyph/color from the API, so derive a decorative one from the
// badge's id/name keywords (purely cosmetic — matches the design's tile look).
function badgeStyle(b: Badge): { symbol: string; color: string } {
  const key = `${b.id} ${b.name}`.toLowerCase();
  if (/feed|care|first/.test(key)) return { symbol: "🪙", color: theme.color.satiety };
  if (/streak|day/.test(key)) return { symbol: "◈", color: theme.color.happiness };
  if (/riddle|cryptic|solv/.test(key)) return { symbol: "?", color: theme.color.happiness };
  if (/quest|dm|dungeon|nat|crit|20/.test(key)) return { symbol: "⌬", color: theme.color.accent };
  if (/top|rank|champ/.test(key)) return { symbol: "★", color: theme.color.accent };
  return { symbol: "🦝", color: theme.color.accent };
}

export function Profile() {
  const { data, isLoading, error } = useMe();

  if (isLoading) return <Loading label="loading profile…" />;
  if (error) return <ErrorNotice message={error.message} />;
  if (!data) return null;

  const name = data.displayName ?? "player";
  const initial = name.charAt(0).toUpperCase();
  const photoUrl = getUserPhotoUrl();
  const earnedCount = data.badges.filter((b) => b.earned).length;

  // Activity graph (oldest → newest). Weeks are columns; pad the leading cells
  // with empties so the grid is always a full HEAT_WEEKS-wide rectangle ending
  // today (bottom-right). Cell intensity is a smooth ramp relative to the
  // busiest day.
  const days: HeatmapCell[] = data.heatmap.slice(-HEAT_DAYS);
  const heatPad = HEAT_DAYS - days.length;
  const maxCount = Math.max(1, ...days.map((c) => c.count));
  const heatAlpha = (count: number) =>
    count <= 0 ? 0 : Math.min(1, 0.18 + (count / maxCount) * 0.7);

  return (
    <Stack>
      <Section>
        <SectionLabel>// stats · {name}</SectionLabel>
        <IdentityCard>
          {photoUrl ? (
            <AvatarImg src={photoUrl} alt={name} referrerPolicy="no-referrer" />
          ) : (
            <Avatar>{initial}</Avatar>
          )}
          <IdentityBody>
            <Handle>{name}</Handle>
            <Sub>
              {data.totalActions} actions · {data.streak}d streak
            </Sub>
            <RankChips>
              {CATEGORIES.map((cat) => (
                <Chip key={cat}>
                  {cat} {rankLabel(data.ranks.chat?.[cat])} · {rankLabel(data.ranks.global[cat])}
                </Chip>
              ))}
            </RankChips>
          </IdentityBody>
        </IdentityCard>
      </Section>

      <Section>
        <Header>
          <SectionLabel>// scores</SectionLabel>
          <Note>all-time</Note>
        </Header>
        <Scores>
          {CATEGORIES.map((cat) => (
            <ScoreCell key={cat}>
              <ScoreHead>
                <ScoreIcon $color={CAT_META[cat].color}>{CAT_META[cat].icon}</ScoreIcon>
                <ScoreLabel>{cat}</ScoreLabel>
              </ScoreHead>
              <ScoreVal>{data.scores[cat].toLocaleString()}</ScoreVal>
              <ScoreUnderline $color={CAT_META[cat].color} />
            </ScoreCell>
          ))}
        </Scores>
      </Section>

      <Section>
        <Header>
          <SectionLabel>// activity</SectionLabel>
          <Note>60 days</Note>
        </Header>
        <Card>
          <ContribGrid $cols={HEAT_WEEKS}>
            {Array.from({ length: heatPad }, (_, i) => (
              <HeatCell key={`pad-${i}`} $alpha={0} />
            ))}
            {days.map((cell) => (
              <HeatCell
                key={cell.date}
                $alpha={heatAlpha(cell.count)}
                title={`${cell.date}: ${cell.count}`}
              />
            ))}
          </ContribGrid>
          <ContribFoot>
            <FootText>
              {data.totalActions} actions · {data.streak}-day streak
            </FootText>
            <Legend>
              less
              {LEGEND_ALPHAS.map((a) => (
                <Swatch key={a} $alpha={a} />
              ))}
              more
            </Legend>
          </ContribFoot>
        </Card>
      </Section>

      <Section>
        <Header>
          <SectionLabel>// badges</SectionLabel>
          <Note>
            {earnedCount} of {data.badges.length}
          </Note>
        </Header>
        <Badges>
          {data.badges.map((b: Badge) => {
            const { symbol, color } = badgeStyle(b);
            return (
              <BadgeCell key={b.id} $earned={b.earned} $color={color} title={b.description}>
                <BadgeSym $earned={b.earned}>{symbol}</BadgeSym>
                <BadgeName $earned={b.earned} $color={color}>
                  {b.name}
                </BadgeName>
              </BadgeCell>
            );
          })}
        </Badges>
      </Section>
    </Stack>
  );
}
